import React, { useEffect, useRef, useState } from "react";
import { eye_icon, logo, Password_View } from "../../assets/img";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useNavigate } from "react-router-dom";
import { isValidEmail } from "../redux/api/Constants";
import { URLS } from "../../Services/Constant";
import { post } from "../../Services/ApiHandler";
import { toast } from "react-toastify";
import Loader from "../../components/Loader";
import { updatePermission } from "../redux/api/PermissionManager";

const getIPAddress = async () => {
  try {
    const localIP = await new Promise((resolve, reject) => {
      const pc = new RTCPeerConnection({ iceServers: [] });
      pc.createDataChannel("");
      pc.createOffer().then((offer) => pc.setLocalDescription(offer));

      pc.onicecandidate = (event) => {
        if (!event.candidate) return;
        const ipMatch = event.candidate.candidate.match(
          /(\d{1,3}\.){3}\d{1,3}/,
        );
        if (ipMatch && !ipMatch[0].startsWith("0.")) {
          resolve(ipMatch[0]);
          pc.close();
        }
      };

      setTimeout(() => reject(new Error("WebRTC timeout")), 1000);
    });

    if (localIP) return localIP;
  } catch {
    console.warn("WebRTC blocked (likely Brave), trying public API...");
  }

  try {
    const res = await fetch("https://api4.ipify.org?format=json");
    const data = await res.json();
    return data.ip;
  } catch {
    console.error("Public API also failed");
    return "Unknown IP";
  }
};

const getFirstAllowedRoute = (permissions = []) => {
  const check = (perm) => permissions.includes(perm);

  const orderedPermissions = [
    // Super Admin
    { perm: "CAN-LIST-DASHBOARD", route: "/dashboard" },
    { perm: "CAN-LIST-ADMIN_LIST", route: "/admin" },
    { perm: "CAN-LIST-ADMIN_ROLES", route: "/roles" },

    // User List
    { perm: "CAN-LIST-OWNER", route: "/user-owner" },
    { perm: "CAN-LIST-CUSTOMER", route: "/customer-list" },
    { perm: "CAN-LIST-RECEPTIONIST", route: "/receptionist" },
    { perm: "CAN-LIST-DEACTIVATE", route: "/deactivate" },

    // Blocks
    { perm: "CAN-LIST-ROOMS", route: "/rooms-block" },
    { perm: "CAN-LIST-PROPERTIES", route: "/properties-block" },

    // MMC
    { perm: "CAN-LIST-MMC_TRANSACTION", route: "/mmc-transaction-list" },
    { perm: "CAN-LIST-MMC_SUSPENDED", route: "/mmc-suspended-list" },
    { perm: "CAN-LIST-MMC_CANCELLATION", route: "/mmc-cancellation-list" },
    { perm: "CAN-LIST-MMC_DEACTIVE", route: "/mmc-deactive-list" },

    // Properties
    { perm: "CAN-LIST-OVERALL_PROPERTIES", route: "/overall-properties" },
    { perm: "CAN-LIST-REQUESTED", route: "/properties" },
    {
      perm: "CAN-LIST-DOCUMENT_VERIFICATION",
      route: "/properties-doc-verfication",
    },
    { perm: "CAN-LIST-MMC", route: "/properties-mmc" },
    { perm: "CAN-LIST-REJECTED", route: "/properties-rejected" },

    // Property Flag
    { perm: "CAN-LIST-PROPERTY__BLUE_FLAG", route: "/property-flag-blue" },
    { perm: "CAN-LIST-PROPERTY__RED_FLAG", route: "/property-flag-red" },

    // Master
    { perm: "CAN-LIST-SERVICES", route: "/services" },
    { perm: "CAN-LIST-AMENITIES", route: "/amenities" },
    { perm: "CAN-LIST-PROPERTY_TAGS", route: "/property-tags" },
    { perm: "CAN-LIST-BANNER", route: "/banners" },
    { perm: "CAN-LIST-VIDEO", route: "/vedio" },
    { perm: "CAN-LIST-TIME_SLOT", route: "/time-slot" },
    { perm: "CAN-LIST-HOTEL_CATEGORIES", route: "/hotel-categories" },
    { perm: "CAN-LIST-ROOM_CATEGORIES", route: "/room-categories" },
    { perm: "CAN-LIST-TIPS", route: "/tips" },
    { perm: "CAN-LIST-COMMISSION", route: "/commission" },
    { perm: "CAN-LIST-CHECK_IN", route: "/check-in" },
    { perm: "CAN-LIST-AGE", route: "/minimum-age" },
    { perm: "CAN-LIST-MASTERMMC", route: "/mmc" },
    { perm: "CAN-LIST-LOCATION", route: "/location" },
    { perm: "CAN-LIST-DELETE_ACCOUNT", route: "/delete-account" },
    { perm: "CAN-LIST-HOUSE_POLICY", route: "/house-policy" },
    { perm: "CAN-LIST-TERMS_&_CONDITIONS", route: "/terms-condition" },

    // Wallet
    { perm: "CAN-LIST-WALLETUSER", route: "/wallettransaction" },
    { perm: "CAN-LIST-WALLETOWNER", route: "/wallet-owner" },

    // Others
    { perm: "CAN-LIST-COUPON", route: "/coupon" },
    { perm: "CAN-LIST-BOOKINGS", route: "/booking-list" },
    { perm: "CAN-LIST-TRANSACTION", route: "/transaction-list" },
    { perm: "CAN-LIST-FAILED_TRANSACTION", route: "/failed_transaction" },
    { perm: "CAN-LIST-INVOICE_USER", route: "/invoice-user" },
    { perm: "CAN-LIST-REFER_&_EARN", route: "/referearn" },
    { perm: "CAN-LIST-WISH_LIST", route: "/wishlist" },
    { perm: "CAN-LIST-NOTIFY_ME", route: "/notifyme" },
    { perm: "CAN-LIST-REVIEW", route: "/review" },
    { perm: "CAN-LIST-ABSENT_LIST", route: "/absentlist" },
    { perm: "CAN-LIST-NOTIFICATION", route: "/user-list" },

    // Settings
    { perm: "CAN-LIST-IN-APP_CONTROL", route: "/vow-settings" },
    { perm: "CAN-LIST-MAINTENANCE", route: "/maintenance" },
    { perm: "CAN-LIST-DAILY_LOG_REPORT", route: "/daily-log" },
    { perm: "CAN-LIST-SMS_GATEWAY", route: "/sms-gateway" },
    { perm: "CAN-LIST-SUPPORT_NUMBER", route: "/whatsapp-details" },
    { perm: "CAN-LIST-OFFICE_IP", route: "/office-ip" },
    { perm: "CAN-LIST-AUDIT_LOG", route: "/audit-log" },
    { perm: "CAN-LIST-PAYMENT_GATEWAY", route: "/payment-gateway" },
  ];

  const found = orderedPermissions.find(({ perm }) => check(perm));
  return found ? found.route : "";
};

const Login = () => {
  const navigate = useNavigate();
  const pass = useRef();
  const [btn, setBtn] = useState(false);
  const [visible, setVisible] = useState(false);
  const [password, setPassword] = useState();
  const [email, setEmail] = useState();
  const [errors, setErrors] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [deviceInfo, setDeviceInfo] = useState();

  const getDeviceInfo = async () => {
    const userAgent = navigator.userAgent;
    let os = "Unknown OS";
    if (/windows/i.test(userAgent)) os = "Windows";
    else if (/android/i.test(userAgent)) os = "Android";
    else if (/iphone|ipad|ipod/i.test(userAgent)) os = "iOS";
    else if (/mac/i.test(userAgent)) os = "MacOS";
    else if (/linux/i.test(userAgent)) os = "Android";

    let browser = "Unknown Browser";
    const isBrave =
      navigator.brave && (await navigator.brave.isBrave()) === true;

    if (isBrave) browser = "Brave";
    else if (/firefox/i.test(userAgent)) browser = "Firefox";
    else if (/edg/i.test(userAgent)) browser = "Edge";
    else if (/opr|opera/i.test(userAgent)) browser = "Opera";
    else if (/chrome/i.test(userAgent)) browser = "Chrome";
    else if (/safari/i.test(userAgent)) browser = "Safari";

    let deviceType = "Desktop";
    if (/mobile/i.test(userAgent)) deviceType = "Mobile";
    else if (/tablet|ipad/i.test(userAgent)) deviceType = "Tablet";

    const ip_address = await getIPAddress();

    const deviceInfo = {
      os,
      browser,
      deviceType,
      ip_address,
      userAgent,
      language: navigator.language,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      online: navigator.onLine,
      platform: navigator.platform,
    };
    setDeviceInfo(deviceInfo);
  };

  useEffect(() => {
    getDeviceInfo();
  }, []);

  const submitHandler = async () => {
    var lowerCase = /[a-z]/g;
    var upperCase = /[A-Z]/g;
    var numbers = /[0-9]/g;

    const regEx = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/;

    if (!email || !regEx.test(email)) {
      toast.error("Enter Valid Email (lowercase only)");
      return;
    }

    if (!password) {
      toast.error("Enter Password");
      return;
    }

    if (!password.match(lowerCase)) {
      toast.error("Password should contain lowercase letters!");
      return;
    } else if (!password.match(upperCase)) {
      toast.error("Password should contain uppercase letters!");
      return;
    } else if (!password.match(numbers)) {
      toast.error("Password should contain numbers also!");
      return;
    } else if (password.length < 8) {
      toast.error("Password length should be more than 8.");
      return;
    }

    setLoading(true);

    let formdata = new FormData();
    formdata.append("email", email);
    formdata.append("password", password);
    formdata.append("ip_address", deviceInfo?.ip_address); // ✅ actual IP
    formdata.append(
      "device_info",
      `${deviceInfo?.os} - ${deviceInfo?.browser}`,
    ); // ✅ OS - Browser

    post(URLS.LOGIN, formdata)
      .then((res) => {
        if (res?.status == "success") {
          toast.success(res?.message);

          if (res?.otp_required) {
            setTimeout(() => {
              navigate("/two-stepverification", {
                state: {
                  type: res,
                  email: email,
                  password: password,
                  ip_address: deviceInfo?.ip_address,
                  device_info: `${deviceInfo?.os} - ${deviceInfo?.browser}`,
                },
              });
            }, 1500);
          } else {
            updatePermission(res?.admin?.permissions);
            localStorage.setItem("token", res?.token);
            localStorage.setItem("admin_details", JSON.stringify(res?.admin));
            localStorage.setItem(
              "permissions",
              JSON.stringify(res?.admin?.permissions),
            );
            setLoading(false);
            toast.success("Login Successfully");
            navigate(getFirstAllowedRoute(res?.admin?.permissions));
          }
        } else {
          setLoading(false);
        }
      })
      .catch((err) => {
        setLoading(false);
        console.log("err", err);
      });
  };

  const keydownHandler = (event) => {
    if (event.key == "Enter") {
      submitHandler();
    }
  };

  return (
    <div className="login-bg">
      <div className="login-left"> </div>
      <div className="login-right">
        <img src={logo} className="logo" alt="" />
        <h2 className="mt-4 f3 fs-sm-16 fs-md-20 fs-lg-22 fs-xl-26 fs-xxl-28 white">
          Welcome Back!!
        </h2>
        <div className="text-start">
          <p className="mt-4 f3 fs-sm-12 fs-md-14 fs-lg-16 fs-xl-17 fs-xxl-20 white">
            Email
          </p>
          <div className="login-input mt-2">
            <MailOutlineRoundedIcon className="emial-icon" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              onKeyDown={keydownHandler}
            />
          </div>
          {errors.email && <p className="err">{errors.email}</p>}
        </div>
        <div className="text-start">
          <p className="mt-4 f3 fs-sm-12 fs-md-14 fs-lg-16 fs-xl-17 fs-xxl-20 white">
            Password
          </p>
          <div className="login-input mt-2">
            <LockOutlinedIcon className="emial-icon" />
            <input
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              type={visible ? "text" : "password"}
              maxLength={20}
              placeholder="Password"
              onKeyDown={keydownHandler}
            />
            {!visible && (
              <img
                src={eye_icon}
                onClick={() => setVisible(!visible)}
                className="eye-icon pointer mb-1"
                alt=""
              />
            )}
            {visible && (
              <div onClick={() => setVisible(!visible)} className="pointer sec">
                <VisibilityIcon className="eye-icon" />
              </div>
            )}
          </div>
          {errors.password && <p className="err">{errors.password}</p>}
        </div>
        <p
          onClick={() => navigate("/forgot-password")}
          className="mt-1 f2 fs-sm-12 fs-md-13 fs-lg-14 fs-xl-16 fs-xxl-18 white pointer text-end"
        >
          Forgot Password?
        </p>
        <button onClick={() => submitHandler()} className="login-btn f4">
          Login
        </button>
      </div>
      {loading && <Loader />}
    </div>
  );
};

export default Login;
